/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  convertReportGoal,
  convertReportSource,
} from '../../../../api/enumerations';
import { updateErrorReport } from '../../../../api/errorReport';
import { ErrorReportData } from '../../../../api/errorReport/types';
import {
  AvmInferenceData,
  AvmFactorsData,
  WorkOrderData,
} from '../../../../api/workOrders/types';
import { MultilineTextField } from '../../../../components/CustomInput';
import { AccordionTitle } from '../../../../components/Sections/AccordionTitle';
import { ClientData } from '../../../../components/Sections/ClientData';
import { PdfViewer } from '../../../../components/Sections/PdfViewer';
import { PropertyAddress } from '../../../../components/Sections/PropertyAddress';
import { PropertyData } from '../../../../components/Sections/PropertyData';
import { PropertyInfo } from '../../../../components/Sections/PropertyInfo';
import { ReportInfo } from '../../../../components/Sections/ReportInfo';
import { Title } from '../../../../components/Sections/Title';
import { ToogleAccordion } from '../../../../components/Sections/ToogleAccordion';
import { BoxContainer } from '../../../../components/UI/Box';
import { TitleTypography } from '../../../../components/UI/Typography';
import { Constants } from '../../../../constants/errorReport';
import {
  IconApartmentMS,
  IconDescriptionMS,
  IconEditDocumentMS,
  IconEngineeringMS,
  IconInventoryMS,
  IconLocationCityMS,
  IconLocationOnMS,
  IconWarningMS,
} from '../../../../constants/icons';
import { GlobalContext } from '../../../../context/global';
import { convertDate } from '../../../../helpers';
import { useAccordion } from '../../../../hooks/useAccordion';
import useErrorMessage from '../../../../hooks/useErrorMessage';
import {
  CreatedStyle,
  DescriptionTypography,
  FlexBox,
  FlexGrid,
  OSNumber,
  SectionBox,
  SectionTitle,
  SubmitBox,
  TitleBox,
} from '../styles';

interface RevisionProps {
  errorReport: ErrorReportData;
  propertyData: WorkOrderData;
  errorFindings: string | undefined;
}

export function Revision({
  errorReport,
  propertyData,
  errorFindings,
}: RevisionProps): JSX.Element {
  const [hasAvmReport, setHasAvmReport] = useState(false);
  const [findings, setFindings] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const reportId = parseInt(id as string, 10);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const {
    expandOne,
    setExpandOne,
    expandTwo,
    setExpandTwo,
    expandThree,
    setExpandThree,
    expandEight,
    setExpandEight,
    expandNine,
    setExpandNine,
    expandAll,
    setExpandAll,
    toggleAccordions,
  } = useAccordion();
  const { getErrorMessage } = useErrorMessage();

  useEffect(() => {
    if (expandOne && expandTwo && expandThree && expandEight && expandNine) {
      setExpandAll(true);
    } else {
      setExpandAll(false);
    }
  }, [expandOne, expandTwo, expandThree, expandEight, expandNine]);

  const getDataCallback = useCallback(async () => {
    const avmData = propertyData.avm_report as
      | AvmInferenceData
      | AvmFactorsData
      | null;

    if (avmData) {
      setHasAvmReport(true);
    }
  }, []);

  useEffect(() => {
    if (errorFindings) {
      setFindings(errorFindings);
    }
    setExpandNine(true);
    getDataCallback();
  }, [getDataCallback]);

  const handleUpdateReport = async (): Promise<void> => {
    if (findings.length < 3) {
      setSnackbarMessage('Adicione observações!');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    const reportData = {
      findings,
      finished: isUpdated,
    };

    try {
      const response = await updateErrorReport(reportId, reportData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== 0) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage(
        isUpdated ? 'Erro finalizado!' : 'Dados alterados com sucesso!'
      );
      setErrorMessage(false);
      setOpenSnackbar(true);
      setIsUpdated(true);
      if (isUpdated) navigate('/error-report');
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  return (
    <BoxContainer>
      <TitleBox>
        <TitleTypography>
          {IconWarningMS}
          {Constants.reportedError}
        </TitleTypography>
        <FlexBox>
          <CreatedStyle>
            {Constants.creation}{' '}
            {errorReport.created_at && convertDate(errorReport.created_at)}
          </CreatedStyle>
          <OSNumber>
            {IconInventoryMS}
            {Constants.osNumber} {errorReport.reference_number}
          </OSNumber>
        </FlexBox>
      </TitleBox>
      <Grid container spacing={5} sx={{ marginBottom: '40px' }}>
        <FlexGrid item xs={5}>
          <Typography>{Constants.reportedBy}</Typography>
          <Typography fontWeight="bold">{errorReport.user_name}</Typography>
        </FlexGrid>
        <FlexGrid item xs={7}>
          <Typography>{Constants.client}</Typography>
          <Typography fontWeight="bold">
            {errorReport.requester_name}
          </Typography>
        </FlexGrid>
        <FlexGrid item xs={5}>
          <Typography>{Constants.goal}</Typography>
          <Typography fontWeight="bold">
            {errorReport.report_goal &&
              convertReportGoal(errorReport.report_goal)}
          </Typography>
        </FlexGrid>
        <FlexGrid item xs={7}>
          <Typography>{Constants.source}</Typography>
          <Typography fontWeight="bold">
            {errorReport.report_source &&
              convertReportSource(errorReport.report_source)}
          </Typography>
        </FlexGrid>
        <FlexGrid item xs={12}>
          <Typography>{Constants.errorDescription}</Typography>
          <DescriptionTypography>
            {errorReport.error_description}
          </DescriptionTypography>
        </FlexGrid>
        {errorReport.finished && (
          <FlexGrid item xs={12}>
            <Typography>{Constants.status}</Typography>
            <Typography color="primary.light" fontWeight="bold">
              finalizado
            </Typography>
          </FlexGrid>
        )}
      </Grid>
      <Divider />
      <Title
        osNumber={0}
        newOs
        title={Constants.revision}
        createdAt={propertyData?.created_at}
      />
      <ClientData propertyData={propertyData} />
      <Box>
        <ToogleAccordion expand={expandAll} toogle={toggleAccordions} />
        <SectionBox>
          <AccordionTitle
            title={Constants.propertyData}
            icon={IconApartmentMS}
            openAccordion={expandOne}
            setOpenAccordion={setExpandOne}
          />
          {expandOne && <PropertyData propertyData={propertyData} />}
        </SectionBox>
        <SectionBox>
          <AccordionTitle
            title={Constants.propertyAddress}
            icon={IconLocationOnMS}
            openAccordion={expandTwo}
            setOpenAccordion={setExpandTwo}
          />
          {expandTwo && <PropertyAddress propertyData={propertyData} />}
        </SectionBox>
        <SectionBox>
          <AccordionTitle
            title={Constants.propertyDetails}
            icon={IconLocationCityMS}
            openAccordion={expandThree}
            setOpenAccordion={setExpandThree}
          />
          {expandThree && <PropertyInfo propertyData={propertyData} />}
        </SectionBox>
        <SectionBox>
          <AccordionTitle
            title={Constants.report}
            icon={IconEngineeringMS}
            openAccordion={expandEight}
            setOpenAccordion={setExpandEight}
          />
          {expandEight && (
            <Box>
              {hasAvmReport && propertyData ? (
                <ReportInfo propertyData={propertyData} />
              ) : (
                <Box>{Constants.noReport}</Box>
              )}
            </Box>
          )}
        </SectionBox>
        <SectionBox>
          <AccordionTitle
            title={Constants.pdfReport}
            icon={IconDescriptionMS}
            openAccordion={expandNine}
            setOpenAccordion={setExpandNine}
          />
          {expandNine && <PdfViewer osId={propertyData.id} />}
        </SectionBox>
        <SectionBox>
          <SectionTitle mb="20px">
            {IconEditDocumentMS}
            {Constants.findings}
          </SectionTitle>
          <MultilineTextField
            id="findings"
            label="observações"
            value={findings}
            setValue={setFindings}
          />
        </SectionBox>
        <SubmitBox>
          {isUpdated ? (
            <Button
              disabled={errorReport.finished}
              onClick={handleUpdateReport}
            >
              {Constants.finish}
            </Button>
          ) : (
            <Button
              disabled={errorReport.finished}
              onClick={handleUpdateReport}
            >
              {Constants.save}
            </Button>
          )}
        </SubmitBox>
      </Box>
    </BoxContainer>
  );
}
